<template>
  <Modal
    :open="open"
    :title="t('demoStage.guests.title')"
    :subtitle="t('demoStage.guests.subtitle')"
    size="md"
    :close-label="t('demoStage.guests.close')"
    @close="emit('close')"
  >
    <div class="demo-guests" data-test="demo-guests">
      <!-- Loading: the roster is read when the dialog opens, never before. -->
      <div v-if="phase === 'loading'" class="demo-guests__loading" data-test="demo-guests-loading" role="status">
        <LumiLoader size="panel" />
        <span>{{ t('demoStage.guests.loading') }}</span>
      </div>

      <!-- The roster could not be read, or may not be read at all (an operator, a
           demo that is not live): said plainly, with a retry only where one can help. -->
      <div v-else-if="phase === 'blocked'" class="demo-guests__blocked" data-test="demo-guests-blocked">
        <InlineNotice :variant="blockedKind === 'generic' ? 'error' : 'warning'" :data-kind="blockedKind">
          {{ t(MESSAGE_KEYS[blockedKind]) }}
        </InlineNotice>
        <div v-if="blockedKind === 'generic'">
          <Button variant="secondary" size="sm" data-test="demo-guests-retry" @click="load">{{ t('demoStage.guests.retry') }}</Button>
        </div>
      </div>

      <template v-else>
        <!-- What a guest is, in plain words, before anyone is invited. -->
        <section class="demo-guests__about" data-test="demo-guests-about">
          <h4 class="demo-guests__list-title">{{ t('demoStage.guests.about.title') }}</h4>
          <ul class="demo-guests__about-list">
            <li>{{ t('demoStage.guests.about.sees', { reset: t('demoStage.reset') }) }}</li>
            <li>{{ t('demoStage.guests.about.resets') }}</li>
            <li>{{ t('demoStage.guests.about.limits') }}</li>
            <li>{{ t('demoStage.guests.about.ends') }}</li>
          </ul>
        </section>

        <form class="demo-guests__form" data-test="demo-guests-form" novalidate @submit.prevent="send(email)">
          <div class="demo-guests__field">
            <Field :label="t('demoStage.guests.emailLabel')" :error="fieldError" :hint="exhausted ? undefined : slotsHint">
              <Input
                v-model="email"
                type="email"
                autocomplete="off"
                :placeholder="t('demoStage.guests.emailPlaceholder')"
                :aria-label="t('demoStage.guests.emailLabel')"
                :disabled="exhausted || sending"
                :invalid="!!fieldError"
                data-test="demo-guests-email"
              />
            </Field>
          </div>
          <Button class="demo-guests__send" type="submit" variant="primary" :loading="sending" :disabled="exhausted || sending || !email.trim()" data-test="demo-guests-send">
            {{ t('demoStage.guests.send') }}
          </Button>
        </form>

        <!-- Exhausted: three of three. The input above is off; the way on is below. -->
        <InlineNotice v-if="exhausted" variant="info" data-test="demo-guests-exhausted">
          {{ t('demoStage.guests.exhausted', { limit: roster.limit }) }}
        </InlineNotice>

        <InlineNotice
          v-if="feedback"
          :variant="feedback.variant"
          data-test="demo-guests-feedback"
          :data-kind="feedback.kind"
          role="status"
        >
          {{ t(MESSAGE_KEYS[feedback.kind], { email: feedback.email ?? '' }) }}
          <Button
            v-if="feedback.kind === 'undelivered' && feedback.email"
            class="demo-guests__again"
            variant="secondary"
            size="sm"
            :disabled="sending"
            data-test="demo-guests-send-again"
            @click="send(feedback.email)"
          >
            {{ t('demoStage.guests.sendAgain') }}
          </Button>
        </InlineNotice>

        <section class="demo-guests__list" aria-live="polite">
          <div class="demo-guests__list-head">
            <h4 class="demo-guests__list-title">{{ t('demoStage.guests.listTitle') }}</h4>
            <span class="demo-guests__used" data-test="demo-guests-used">{{ t('demoStage.guests.used', { used: roster.used, limit: roster.limit }) }}</span>
          </div>
          <EmptyState v-if="roster.guests.length === 0" :title="t('demoStage.guests.empty')" data-test="demo-guests-empty" />
          <ul v-else class="demo-guests__rows">
            <li
              v-for="guest in roster.guests"
              :key="guest.userId"
              class="demo-guests__row"
              data-test="demo-guests-row"
              :data-user-id="guest.userId"
              :data-state="guest.state"
            >
              <span class="demo-guests__address" data-test="demo-guests-address">{{ guest.email }}</span>
              <Badge :tone="guest.state === 'joined' ? 'success' : 'neutral'" data-test="demo-guests-state">
                {{ t(STATE_KEYS[guest.state]) }}
              </Badge>
              <!-- A member this door may not remove (a workspace admin) carries no revoke. -->
              <Button
                v-if="guest.revocable"
                variant="ghost"
                size="sm"
                :loading="revoking === guest.userId"
                :disabled="revoking !== null || sending"
                :aria-label="t('demoStage.guests.revokeFor', { email: guest.email })"
                data-test="demo-guests-revoke"
                @click="revoke(guest)"
              >
                {{ revoking === guest.userId ? t('demoStage.guests.revoking') : t('demoStage.guests.revoke') }}
              </Button>
            </li>
          </ul>
        </section>
      </template>
    </div>

    <template #footer>
      <Button variant="secondary" data-test="demo-guests-close" @click="emit('close')">{{ t('demoStage.guests.close') }}</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Badge, Button, EmptyState, Field, InlineNotice, Input, Modal, LumiLoader } from '@lumi/ui';
import type { DemoGuest, DemoGuestOutcome, DemoGuestRoster, DemoGuestsApi } from '../composables/useDemoGuests';

// "Invite a colleague" (issue 669, ADR 0030 revision 2026-10-04): the demo bar's one
// door for real people. One address, the colleagues so far (invited / joined), "n of 3"
// used and a revoke per row. The roster is read here, when an admin opens the dialog —
// it carries addresses and never rides in the shared onboarding status. user-api owns
// every rule (the cap, the rate limit, who may invite); this words its answers, state
// by state, and never shows a raw code or message. The short "what a guest is" list sits
// above the form: what she sees, that she may reset, what she can never touch, when it ends.
const props = defineProps<{ open: boolean; api: DemoGuestsApi }>();
const emit = defineEmits<{ close: [] }>();

const { t } = useI18n();

type MessageKind =
  | 'sent'
  | 'resent'
  | 'undelivered'
  | 'revoked'
  | 'alreadyMember'
  | 'limit'
  | 'rateLimited'
  | 'notLive'
  | 'operator'
  | 'operatorNotMember'
  | 'notAPerson'
  | 'forbidden'
  | 'noticeIncomplete'
  | 'accountClosed'
  | 'unavailable'
  | 'revokeFailed'
  | 'generic';
type Feedback = { kind: MessageKind; variant: 'success' | 'info' | 'warning' | 'error'; email?: string };

// Full literal keys (not built from the code) so the i18n guards can see every use.
const MESSAGE_KEYS: Record<MessageKind, string> = {
  sent: 'demoStage.guests.feedback.sent',
  resent: 'demoStage.guests.feedback.resent',
  undelivered: 'demoStage.guests.feedback.undelivered',
  revoked: 'demoStage.guests.feedback.revoked',
  alreadyMember: 'demoStage.guests.feedback.alreadyMember',
  limit: 'demoStage.guests.feedback.limit',
  rateLimited: 'demoStage.guests.feedback.rateLimited',
  notLive: 'demoStage.guests.feedback.notLive',
  operator: 'demoStage.guests.feedback.operator',
  operatorNotMember: 'demoStage.guests.feedback.operatorNotMember',
  notAPerson: 'demoStage.guests.feedback.notAPerson',
  forbidden: 'demoStage.guests.feedback.forbidden',
  noticeIncomplete: 'demoStage.guests.feedback.noticeIncomplete',
  accountClosed: 'demoStage.guests.feedback.accountClosed',
  unavailable: 'demoStage.guests.feedback.unavailable',
  revokeFailed: 'demoStage.guests.feedback.revokeFailed',
  generic: 'demoStage.guests.feedback.generic',
};
const STATE_KEYS: Record<DemoGuest['state'], string> = {
  invited: 'demoStage.guests.state.invited',
  joined: 'demoStage.guests.state.joined',
};

// The server's codes, as the contract lists them (the reset's four state refusals
// share one sentence: the demo is not live).
const KIND_BY_CODE: Record<string, MessageKind> = {
  DEMO_GUEST_ALREADY_MEMBER: 'alreadyMember',
  demo_guest_limit_reached: 'limit',
  RATE_LIMITED: 'rateLimited',
  INVITE_RATE_LIMITED: 'rateLimited',
  MEMBERSHIP_GATE_UNAVAILABLE: 'unavailable',
  EMPLOYEE_NOTICE_INCOMPLETE: 'noticeIncomplete',
  account_closed: 'accountClosed',
  ONBOARDING_ALREADY_SET_UP: 'notLive',
  ONBOARDING_SEEDING_IN_PROGRESS: 'notLive',
  ONBOARDING_COMMIT_IN_PROGRESS: 'notLive',
  ONBOARDING_NOT_IN_DEMO: 'notLive',
  DEMO_GUESTS_OPERATOR: 'operator',
  // The invited ADDRESS is a Lumi operator's account / not a person's at all
  // (user-api asks the target; DEMO_GUESTS_OPERATOR asks the requester).
  OPERATOR_NOT_MEMBER: 'operatorNotMember',
  DEMO_GUEST_NOT_A_PERSON: 'notAPerson',
  DEMO_GUESTS_FORBIDDEN: 'forbidden',
};
function kindOf(outcome: { status?: number; code?: string }): MessageKind {
  const byCode = outcome.code ? KIND_BY_CODE[outcome.code] : undefined;
  if (byCode) return byCode;
  return outcome.status === 429 ? 'rateLimited' : 'generic';
}

// A light shape check so an obvious typo costs no round trip; user-api decides.
const ADDRESS_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const phase = ref<'loading' | 'ready' | 'blocked'>('loading');
const blockedKind = ref<MessageKind>('generic');
const roster = ref<DemoGuestRoster>({ limit: 3, used: 0, guests: [] });
const email = ref('');
const fieldError = ref('');
const feedback = ref<Feedback | null>(null);
const sending = ref(false);
const revoking = ref<string | null>(null);

const exhausted = computed(() => roster.value.used >= roster.value.limit);
const slotsLeft = computed(() => Math.max(0, roster.value.limit - roster.value.used));
const slotsHint = computed(() => t('demoStage.guests.slotsLeft', { count: slotsLeft.value }, slotsLeft.value));

function block(outcome: Extract<DemoGuestOutcome<unknown>, { ok: false }>) {
  const kind = kindOf(outcome);
  // Only the refusals that end the dialog's usefulness block it; anything else is a
  // read that failed, which a retry may fix.
  blockedKind.value = kind === 'notLive' || kind === 'operator' || kind === 'forbidden' ? kind : 'generic';
  phase.value = 'blocked';
}

let loadToken = 0;
async function load() {
  const token = ++loadToken;
  phase.value = 'loading';
  const outcome = await props.api.fetchDemoGuests();
  if (token !== loadToken) return;
  if (outcome.ok) {
    roster.value = outcome.data;
    phase.value = 'ready';
  } else {
    block(outcome);
  }
}

// Every opening starts clean and reads the roster afresh; closing drops the
// addresses from memory.
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      email.value = '';
      fieldError.value = '';
      feedback.value = null;
      revoking.value = null;
      void load();
    } else {
      loadToken++;
      roster.value = { limit: roster.value.limit, used: 0, guests: [] };
    }
  },
  { immediate: true },
);

async function send(address: string) {
  const target = address.trim().toLowerCase();
  // The input and the button are off while the demo is full, but "Send again" on an
  // open invite stays possible: it holds its slot already.
  if (sending.value) return;
  feedback.value = null;
  fieldError.value = '';
  if (!ADDRESS_SHAPE.test(target)) {
    fieldError.value = t('demoStage.guests.invalid');
    return;
  }
  sending.value = true;
  try {
    const outcome = await props.api.inviteDemoGuest(target);
    if (outcome.ok) {
      roster.value = outcome.data;
      email.value = '';
      if (!outcome.data.emailDelivered) feedback.value = { kind: 'undelivered', variant: 'warning', email: target };
      else feedback.value = outcome.data.resent ? { kind: 'resent', variant: 'info', email: target } : { kind: 'sent', variant: 'success', email: target };
      return;
    }
    if (outcome.code === 'DEMO_GUEST_EMAIL_INVALID') {
      fieldError.value = t('demoStage.guests.invalid');
      return;
    }
    const kind = kindOf(outcome);
    feedback.value = { kind, variant: 'error', email: target };
    // The demo moved under the dialog (full, or no longer live): re-read, so the
    // list and the counter say what is true now.
    if (kind === 'limit' || kind === 'alreadyMember') await refreshQuietly();
  } finally {
    sending.value = false;
  }
}

// A refresh after a refusal keeps the explanation on screen; a failed one keeps
// the roster as it was.
async function refreshQuietly() {
  const outcome = await props.api.fetchDemoGuests();
  if (outcome.ok) roster.value = outcome.data;
}

async function revoke(guest: DemoGuest) {
  if (revoking.value || sending.value) return;
  feedback.value = null;
  revoking.value = guest.userId;
  try {
    const outcome = await props.api.revokeDemoGuest(guest.userId);
    if (outcome.ok) {
      roster.value = outcome.data;
      feedback.value = { kind: 'revoked', variant: 'success', email: guest.email };
      return;
    }
    // Already gone (another admin was quicker): the list is the answer.
    if (outcome.status === 404) {
      await refreshQuietly();
      feedback.value = { kind: 'revoked', variant: 'success', email: guest.email };
      return;
    }
    const kind = kindOf(outcome);
    feedback.value = { kind: kind === 'generic' ? 'revokeFailed' : kind, variant: 'error', email: guest.email };
    await refreshQuietly();
  } finally {
    revoking.value = null;
  }
}
</script>

<style scoped>
.demo-guests {
  display: grid;
  gap: var(--space-4);
  font-size: var(--text-sm);
  color: var(--text-body);
}
.demo-guests__loading {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  color: var(--text-secondary);
}
.demo-guests__blocked {
  display: grid;
  gap: var(--space-3);
}
.demo-guests__about {
  display: grid;
  gap: var(--space-2);
  padding: var(--space-3);
  border-radius: var(--radius-md);
  background: var(--surface-wash);
}
.demo-guests__about-list {
  display: grid;
  gap: var(--space-1);
  margin: 0;
  padding-left: var(--space-4);
  color: var(--text-secondary);
}
.demo-guests__form {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
}
.demo-guests__field {
  flex: 1;
  min-width: 0;
}
/* The label sits above the input, so the button is pushed down to its row. */
.demo-guests__send {
  margin-top: 26px;
}
.demo-guests__again {
  margin-left: var(--space-3);
}
.demo-guests__list {
  display: grid;
  gap: var(--space-2);
}
.demo-guests__list-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-3);
}
.demo-guests__list-title {
  margin: 0;
  font-size: var(--text-label);
  font-weight: var(--weight-semibold);
  color: var(--text-heading);
}
.demo-guests__used {
  font-size: var(--text-xs);
  color: var(--text-secondary);
}
.demo-guests__rows {
  display: grid;
  margin: 0;
  padding: 0;
  list-style: none;
}
.demo-guests__row {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) 0;
  border-top: 1px solid var(--border-default);
}
.demo-guests__row:first-child {
  border-top: 0;
}
.demo-guests__address {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-heading);
}
</style>
